/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';


interface ValidationAndQualityReleaseSectionProps {
  batchDetails?: any;
  powderBatchDetails?: any;
}

const ValidationAndQualityReleaseSection: React.FC<
  ValidationAndQualityReleaseSectionProps
> = ({batchDetails, powderBatchDetails }) => {
  const { t } = useTranslation();

  const qualityReviewDetails = batchDetails?.final_quality_review?.details || [];
  const gptTest = powderBatchDetails?.data?.find((lot: any) => lot.gpt_tests && lot.gpt_tests.length > 0)?.gpt_tests?.[0];
  const organisms = gptTest?.organisms || [];
  const ReviewDetails = batchDetails?.final_quality_review?.master;
  const reviewerName = ReviewDetails?.reviewed_by_name || "-";
  const reviewDate = ReviewDetails?.created_at || "-";
  const remarks = ReviewDetails?.remarks || "-";
  
  
  const gptstatus = gptTest?.qa_status || "-";
  const VCTstatus = ReviewDetails?.overall_status || "-";
  const sterlizationStatus = batchDetails?.sterilization_details?.steam_clox_indicator_result  || "-";
  const isGptPassed = gptstatus === "RELEASED";
  const isSterilityPassed = sterlizationStatus === "PASS";
  const isVctPassed = VCTstatus === "Approved";
  const status = batchDetails?.status || "-";
  const isQaReleased = status === "Ready to Use";


  return (  
  <section className="bg-white border border-slate-200 rounded-lg overflow-hidden w-full">
    <div className="flex items-center bg-[#F8FAFC] gap-4 px-6 py-2" style={{ borderBottom: "1px solid #D6DEE8" }}>
      <div className="w-6 h-6 rounded-sm bg-slate-900 text-white flex items-center justify-center font-black text-[10px]">07</div>
      <h3 className="text-[13px] font-bold uppercase tracking-wide text-[#1E2B4D] mt-[7px]">{t("mediaPrepBatch.mediaPrepBatchDetails.Validation")}</h3>
    </div>

    <div className="p-4 grid grid-cols-3 gap-6 items-start w-full">

      {/* Quality Control Tests */}
      <div className="border border-slate-200 rounded-lg overflow-hidden w-full">
        <div className="px-3 py-2.5 bg-[#F8FAFC]" style={{ borderBottom: "1px solid #D6DEE8" }}>
          <h4 className="w-full text-left text-[12px] font-bold uppercase text-[#1E2B4D]">{t("mediaPrepBatch.mediaPrepBatchDetails.QUALITY CONTROL TESTS")}</h4>
        </div>

        <div className="px-3">
          <div className="flex justify-between items-center h-13" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <p className="text-[11px] font-semibold text-slate-500 uppercase whitespace-nowrap mt-[14px]">{t("mediaPrepBatch.mediaPrepBatchDetails.gptTest")}</p>
            <div className="flex items-center gap-2">
              <span className={`material-symbols-outlined text-[18px] ${isGptPassed ? "text-green-600" : "text-slate-400"}`}>
                {isGptPassed ? "check_circle" : "radio_button_unchecked"}
              </span>
              <span className={`text-[11.5px] font-bold ${isGptPassed ? "text-green-600" : "text-slate-500"}`}>
                {isGptPassed ? "Pass" : gptstatus}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center h-13" style={{ borderBottom: "1px solid #D6DEE8" }}>
            <p className="text-[11.5px] font-semibold text-slate-500 uppercase whitespace-nowrap mt-[14px]">{t("mediaPrepBatch.mediaPrepBatchDetails.completed")}</p>
            <div className="flex items-center gap-2">
              <span
                className={`material-symbols-outlined text-[18px] ${isSterilityPassed ? "text-green-600" : "text-[#C5CDD8]"}`}
                style={{ fontVariationSettings: "'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 24" }}
              >
                {t("mediaPrepBatch.mediaPrepBatchDetails.check_circle")}
              </span>
              <span className={`text-[11.5px] font-bold ${isSterilityPassed ? "text-green-600" : "text-slate-500"}`}>
                {isSterilityPassed ? "Pass" : sterlizationStatus}
              </span>
            </div>
          </div>


          <div className="flex justify-between items-center h-13">
            <p className="text-[11px] font-semibold text-slate-500 uppercase whitespace-nowrap mt-[14px]">{t("mediaPrepBatch.mediaPrepBatchDetails.VisualClarityTest")}</p>
            <div className="flex items-center gap-2">
              <span
                className={`material-symbols-outlined text-[18px] ${isVctPassed ? "text-green-600" : "text-[#C5CDD8]"}`}
                style={{ fontVariationSettings: "'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 24" }}
              >
                {t("mediaPrepBatch.mediaPrepBatchDetails.check_circle")}
              </span>
              <span className={`text-[11.5px] font-bold ${isVctPassed ? "text-green-600" : "text-slate-500"}`}>
                {isVctPassed ? "Pass" : VCTstatus}
              </span>
            </div>
          </div>

        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden w-full">
        <div className="px-1 py-2.5 bg-[#F8FAFC]" style={{ borderBottom: "1px solid #D6DEE8" }}>
          <h4 className="text-[12px] font-bold uppercase text-[#1E2B4D] text-center">{t("mediaPrepBatch.mediaPrepBatchDetails.BATCH STATUS")}</h4>
        </div>

        <div className="m-3 rounded-md border border-[#D9EFD9] bg-[#F5FBF5] overflow-hidden">
          <div className="flex items-center justify-center gap-4 py-1.5" style={{ borderBottom: "1px solid #E4EFE4" }}>
            <span className={`material-symbols-outlined text-[28px] ${isQaReleased ? "text-[#1E8E3E]" : "text-[#C5CDD8]"}`}>{t("mediaPrepBatch.mediaPrepBatchDetails.science")}</span>
            <span className={`text-[13px] font-bold uppercase ${isQaReleased ? "text-[#1E8E3E]" : "text-[#9CA3AF]"}`}>
              {isQaReleased ? "QA RELEASED" : status}
            </span>
          </div>

          <div className="px-4 py-2.5">
            <div className="flex items-center">
              <span className="text-[13px] text-[#4B5563] w-[90px]">{t("mediaPrepBatch.mediaPrepBatchDetails.Approved by")}</span>
              <span className="text-[13px] font-semibold text-[#1E2B4D]">{reviewerName}</span>
            </div>

            <div className="flex items-center mt-2">
              <span className="text-[13px] text-[#4B5563] w-[90px]">On</span>
              <span className="text-[13px] font-bold text-[#1E8E3E] whitespace-nowrap">{reviewDate}</span>
            </div>
          </div>
        </div>
      </div>


      {/* Release Details */}
      <div className="border border-slate-200 rounded-lg overflow-hidden w-full">
        <div className="px-3 py-2.5 bg-[#F8FAFC]" style={{ borderBottom: "1px solid #D6DEE8" }}>
          <h4 className="text-[12px] font-bold uppercase text-[#1E2B4D]">{t("mediaPrepBatch.mediaPrepBatchDetails.RELEASE DETAILS")}</h4>
        </div>

        <div className="px-4">
          <div className="flex items-center py-4" style={{ borderBottom: "1px solid #E5E7EB" }}>
            <span className="w-[140px] text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.Release Date")}</span>
            <span className="text-[13px] font-semibold text-[#1E2B4D] whitespace-nowrap">
              {reviewDate}
            </span>
          </div>

          <div className="flex py-4">
            <span className="w-[140px] text-[11px] font-semibold uppercase text-slate-500">{t("mediaPrepBatch.mediaPrepBatchDetails.Remarks")}</span>
            <span className="text-[13px] font-semibold text-[#1E2B4D] leading-5">
              {remarks}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
)};
  
export default ValidationAndQualityReleaseSection;
